import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

function ExportModal({ categories, transactions, onClose }) {
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    amountMin: '',
    amountMax: '',
    merchants: [],
    categories: [],
    recurringOnly: false,
    nonRecurringOnly: false,
  });
  const [availableMerchants, setAvailableMerchants] = useState([]);

  useEffect(() => {
    // Get unique merchants from transactions
    const merchants = [...new Set(transactions.map(t => t.merchant))].sort();
    setAvailableMerchants(merchants);
  }, [transactions]);

  function handleMerchantToggle(merchant) {
    setFilters(prev => ({
      ...prev,
      merchants: prev.merchants.includes(merchant)
        ? prev.merchants.filter(m => m !== merchant)
        : [...prev.merchants, merchant]
    }));
  }

  function handleCategoryToggle(categoryId) {
    setFilters(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter(c => c !== categoryId)
        : [...prev.categories, categoryId]
    }));
  }

  async function handleExport() {
    try {
      const exportData = {
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        amountMin: filters.amountMin ? parseFloat(filters.amountMin) : undefined,
        amountMax: filters.amountMax ? parseFloat(filters.amountMax) : undefined,
        merchants: filters.merchants.length > 0 ? filters.merchants : undefined,
        categories: filters.categories.length > 0 ? filters.categories : undefined,
        recurringOnly: filters.recurringOnly || undefined,
        nonRecurringOnly: filters.nonRecurringOnly || undefined,
      };

      const blob = await api.exportTransactions(exportData);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      onClose();
    } catch (error) {
      alert('Failed to export transactions: ' + error.message);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Export Transactions to CSV</h2>
          <button onClick={onClose} className="modal-close">✕</button>
        </div>

        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label>From Date</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="input-text"
              />
            </div>

            <div className="form-group">
              <label>To Date</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="input-text"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Min Amount</label>
              <input
                type="number"
                step="0.01"
                value={filters.amountMin}
                onChange={(e) => setFilters({ ...filters, amountMin: e.target.value })}
                className="input-text"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label>Max Amount</label>
              <input
                type="number"
                step="0.01"
                value={filters.amountMax}
                onChange={(e) => setFilters({ ...filters, amountMax: e.target.value })}
                className="input-text"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Merchants</label>
            <div className="checkbox-list">
              {availableMerchants.length === 0 && (
                <p className="empty-state">No merchants available</p>
              )}
              {availableMerchants.map(merchant => (
                <label key={merchant} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={filters.merchants.includes(merchant)}
                    onChange={() => handleMerchantToggle(merchant)}
                  />
                  <span>{merchant}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Categories</label>
            <div className="checkbox-list">
              {categories.length === 0 && (
                <p className="empty-state">No categories available</p>
              )}
              {categories.map(category => (
                <label key={category.id} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={filters.categories.includes(category.id)}
                    onChange={() => handleCategoryToggle(category.id)}
                  />
                  <span>{category.name} ({category.type})</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Recurring Transactions</label>
            <div className="form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={filters.recurringOnly}
                  onChange={(e) => setFilters({ 
                    ...filters, 
                    recurringOnly: e.target.checked,
                    nonRecurringOnly: e.target.checked ? false : filters.nonRecurringOnly
                  })}
                />
                <span>Only recurring transactions</span>
              </label>
            </div>
            <div className="form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={filters.nonRecurringOnly}
                  onChange={(e) => setFilters({ 
                    ...filters, 
                    nonRecurringOnly: e.target.checked,
                    recurringOnly: e.target.checked ? false : filters.recurringOnly
                  })}
                />
                <span>Only non-recurring transactions</span>
              </label>
            </div>
          </div>

          <div className="modal-actions">
            <button onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button onClick={handleExport} className="btn btn-primary">
              Export CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExportModal;
